import { createMemo, createSignal, For, Show } from 'solid-js'
import { type Contour, ccw, isSimple, type Pt, SHAPE_PRESETS, type ShapePresetId } from '../acoustics/polygon'
import { Btn, NumberInput, Segmented } from './controls'
import { round } from './util'

/** Smallest 1-2-5 × 10ⁿ step that is at least `target`. */
function niceStep(target: number): number {
  const pow = 10 ** Math.floor(Math.log10(Math.max(target, 1e-6)))
  for (const m of [1, 2, 5, 10]) if (m * pow >= target) return m * pow
  return 10 * pow
}

const clone = (c: Contour[]): Contour[] => c.map((p) => p.map(([x, z]) => [x, z] as Pt))

export interface OutlineEditorProps {
  /** Outline first, then any holes, in metres on the (x, z) plane. */
  contours: Contour[]
  /** Called with the new contours when an edit finishes and is valid. */
  onCommit: (contours: Contour[]) => void
  allowHoles?: boolean
  /** Grid snap in metres (0 = off). */
  snap: number
  /** Width and depth in metres that a shape preset is scaled to. */
  presetSize: [number, number]
  maxPoints: number
  maxHoles?: number
  testid: string
}

/**
 * Draw and edit a polygon: drag corners, click the + on an edge to add one, or type exact coordinates.
 * The picture is a top view (north up). Edits that would make edges cross are refused.
 */
export function OutlineEditor(props: OutlineEditorProps) {
  const [draft, setDraft] = createSignal<Contour[] | null>(null)
  const [active, setActive] = createSignal(0)
  const [selected, setSelected] = createSignal<number | null>(null)
  const [frozen, setFrozen] = createSignal<[number, number, number, number] | null>(null)
  let svg!: SVGSVGElement
  let drag: { c: number; v: number } | null = null

  const shown = () => draft() ?? props.contours
  const outline = () => shown()[0] ?? []
  const activeContour = () => shown()[Math.min(active(), shown().length - 1)] ?? []
  const valid = () => shown().every((c) => isSimple(c))

  const box = createMemo<[number, number, number, number]>(() => {
    const f = frozen()
    if (f) return f
    let minX = Infinity
    let maxX = -Infinity
    let minZ = Infinity
    let maxZ = -Infinity
    for (const c of props.contours) {
      for (const [x, z] of c) {
        minX = Math.min(minX, x)
        maxX = Math.max(maxX, x)
        minZ = Math.min(minZ, z)
        maxZ = Math.max(maxZ, z)
      }
    }
    if (!Number.isFinite(minX)) return [-5, -5, 10, 10]
    const span = Math.max(maxX - minX, maxZ - minZ, 4)
    const pad = span * 0.2
    return [minX - pad, minZ - pad, maxX - minX + 2 * pad, maxZ - minZ + 2 * pad]
  })

  const unit = () => Math.max(box()[2], box()[3]) / 260 // metres per screen pixel, roughly
  const gridLines = createMemo(() => {
    const [x0, z0, w, h] = box()
    const step = niceStep(Math.max(w, h) / 8)
    const xs: number[] = []
    const zs: number[] = []
    for (let x = Math.ceil(x0 / step) * step; x <= x0 + w; x += step) xs.push(x)
    for (let z = Math.ceil(z0 / step) * step; z <= z0 + h; z += step) zs.push(z)
    return { xs, zs, step }
  })

  const toSvg = (e: PointerEvent): Pt => {
    const p = svg.createSVGPoint()
    p.x = e.clientX
    p.y = e.clientY
    const m = svg.getScreenCTM()
    const q = m ? p.matrixTransform(m.inverse()) : p
    const s = props.snap
    return s > 0 ? [Math.round(q.x / s) * s, Math.round(q.y / s) * s] : [q.x, q.y]
  }

  function beginDrag(e: PointerEvent, c: number, v: number, base: Contour[]) {
    e.stopPropagation()
    ;(e.currentTarget as Element).setPointerCapture(e.pointerId)
    drag = { c, v }
    setFrozen(box())
    setDraft(base)
    setActive(c)
    setSelected(v)
  }

  function onMove(e: PointerEvent) {
    const d = draft()
    if (!drag || !d) return
    const [x, z] = toSvg(e)
    const next = clone(d)
    next[drag.c]![drag.v] = [x, z]
    setDraft(next)
  }

  function endDrag() {
    const d = draft()
    const before = props.contours
    drag = null
    setFrozen(null)
    setDraft(null)
    if (!d) return
    const changed = JSON.stringify(d) !== JSON.stringify(before)
    if (changed && d.every((c) => isSimple(c))) props.onCommit(d)
  }

  const commitIfValid = (next: Contour[]): boolean => {
    if (!next.every((c) => isSimple(c))) return false
    props.onCommit(next)
    return true
  }

  function removeVertex(ci: number, vi: number) {
    const next = clone(props.contours)
    if (next[ci]!.length <= 3) return
    next[ci]!.splice(vi, 1)
    if (commitIfValid(next)) setSelected(null)
  }

  function addVertex() {
    const next = clone(props.contours)
    const c = next[Math.min(active(), next.length - 1)]!
    if (c.length >= props.maxPoints) return
    // Split the longest edge.
    let best = 0
    let bestLen = -1
    c.forEach((a, i) => {
      const b = c[(i + 1) % c.length]!
      const l = Math.hypot(b[0] - a[0], b[1] - a[1])
      if (l > bestLen) {
        bestLen = l
        best = i
      }
    })
    const a = c[best]!
    const b = c[(best + 1) % c.length]!
    c.splice(best + 1, 0, [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2])
    commitIfValid(next)
  }

  function applyPreset(id: ShapePresetId) {
    const [w, d] = props.presetSize
    const outer = ccw(SHAPE_PRESETS[id].points().map(([x, z]) => [x * w, z * d] as Pt))
    const s = props.snap
    const snapped = s > 0 ? outer.map(([x, z]) => [Math.round(x / s) * s, Math.round(z / s) * s] as Pt) : outer
    const next = clone(props.contours)
    next[0] = isSimple(snapped) ? snapped : outer
    setActive(0)
    commitIfValid(next)
  }

  function addHole() {
    const o = props.contours[0] ?? []
    if (!o.length || props.contours.length - 1 >= (props.maxHoles ?? 8)) return
    const xs = o.map((p) => p[0])
    const zs = o.map((p) => p[1])
    const cx = (Math.min(...xs) + Math.max(...xs)) / 2
    const cz = (Math.min(...zs) + Math.max(...zs)) / 2
    const r = Math.min(Math.max(...xs) - Math.min(...xs), Math.max(...zs) - Math.min(...zs)) * 0.15
    const hole: Contour = [
      [cx - r, cz - r],
      [cx + r, cz - r],
      [cx + r, cz + r],
      [cx - r, cz + r],
    ]
    const index = props.contours.length // read before committing: the store updates synchronously
    if (commitIfValid([...clone(props.contours), hole])) setActive(index)
  }

  function removeHole() {
    const i = Math.min(active(), props.contours.length - 1)
    if (i <= 0) return
    setActive(0)
    props.onCommit(clone(props.contours).filter((_, k) => k !== i))
  }

  const setCoord = (ci: number, vi: number, axis: 0 | 1, v: number): boolean => {
    const next = clone(props.contours)
    next[ci]![vi]![axis] = v
    return commitIfValid(next)
  }

  const path = (c: Contour) => (c.length ? `M ${c.map(([x, z]) => `${x} ${z}`).join(' L ')} Z` : '')

  return (
    <div class="outline-editor" data-testid={props.testid}>
      <div class="actions">
        <For each={Object.entries(SHAPE_PRESETS) as [ShapePresetId, { label: string }][]}>
          {([id, p]) => (
            <Btn
              small
              onClick={() => applyPreset(id)}
              title={`Replace the outline with a ${p.label.toLowerCase()}`}
              testid={`${props.testid}-preset-${id}`}
            >
              {p.label}
            </Btn>
          )}
        </For>
      </div>

      <svg
        ref={svg}
        class="outline-editor__svg"
        classList={{ 'is-invalid': !valid() }}
        viewBox={box().join(' ')}
        preserveAspectRatio="xMidYMid meet"
        onPointerMove={onMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        data-testid={`${props.testid}-svg`}
        role="img"
        aria-label="Outline drawing, top view, north up"
      >
        <g class="outline-editor__grid">
          <For each={gridLines().xs}>
            {(x) => (
              <line
                x1={x}
                y1={box()[1]}
                x2={x}
                y2={box()[1] + box()[3]}
                classList={{ 'is-axis': Math.abs(x) < 1e-9 }}
                vector-effect="non-scaling-stroke"
              />
            )}
          </For>
          <For each={gridLines().zs}>
            {(z) => (
              <line
                x1={box()[0]}
                y1={z}
                x2={box()[0] + box()[2]}
                y2={z}
                classList={{ 'is-axis': Math.abs(z) < 1e-9 }}
                vector-effect="non-scaling-stroke"
              />
            )}
          </For>
        </g>
        <path class="outline-editor__fill" d={shown().map(path).join(' ')} fill-rule="evenodd" vector-effect="non-scaling-stroke" />
        <For each={shown()}>
          {(c, ci) => (
            <>
              {/* Edge handles: dragging one inserts a corner there. */}
              <Show when={c.length < props.maxPoints}>
                <For each={c}>
                  {(a, i) => {
                    const b = () => c[(i() + 1) % c.length]!
                    return (
                      <circle
                        class="outline-editor__mid"
                        cx={(a[0] + b()[0]) / 2}
                        cy={(a[1] + b()[1]) / 2}
                        r={unit() * 3.5}
                        onPointerDown={(e) => {
                          const next = clone(props.contours)
                          next[ci()]!.splice(i() + 1, 0, [(a[0] + b()[0]) / 2, (a[1] + b()[1]) / 2])
                          beginDrag(e, ci(), i() + 1, next)
                        }}
                      >
                        <title>Drag to add a corner</title>
                      </circle>
                    )
                  }}
                </For>
              </Show>
              <For each={c}>
                {(p, vi) => (
                  <circle
                    class="outline-editor__vertex"
                    classList={{ 'is-hole': ci() > 0, 'is-selected': ci() === active() && selected() === vi() }}
                    cx={p[0]}
                    cy={p[1]}
                    r={unit() * 5.5}
                    data-testid={`${props.testid}-vertex-${ci()}-${vi()}`}
                    onPointerDown={(e) => beginDrag(e, ci(), vi(), clone(props.contours))}
                  />
                )}
              </For>
            </>
          )}
        </For>
      </svg>
      <Show when={!valid()}>
        <p class="note note--warn">Edges cross: release to cancel, or move the corner somewhere that keeps the outline simple.</p>
      </Show>
      <p class="note">
        Top view, north up. Drag a corner, or drag the small dot on an edge to add one.{' '}
        {round(Math.max(...outline().map((p) => p[0]), 0) - Math.min(...outline().map((p) => p[0]), 0), 1)} ×{' '}
        {round(Math.max(...outline().map((p) => p[1]), 0) - Math.min(...outline().map((p) => p[1]), 0), 1)} m.
      </p>

      <Show when={props.allowHoles}>
        <div class="actions">
          <Show when={props.contours.length > 1}>
            <Segmented
              label="Contour"
              testid={`${props.testid}-contour`}
              value={String(Math.min(active(), props.contours.length - 1))}
              onChange={(v) => setActive(Number(v))}
              options={props.contours.map((_, i) => ({ value: String(i), label: i === 0 ? 'Outline' : `Hole ${i}` }))}
            />
          </Show>
          <Btn small onClick={addHole} testid={`${props.testid}-add-hole`} title="Cut a hole through the shape">
            + Hole
          </Btn>
          <Show when={Math.min(active(), props.contours.length - 1) > 0}>
            <Btn small danger onClick={removeHole} testid={`${props.testid}-remove-hole`}>
              − Hole
            </Btn>
          </Show>
        </div>
      </Show>

      <div class="outline-editor__table" data-testid={`${props.testid}-table`}>
        <div class="outline-editor__row outline-editor__row--head">
          <span>#</span>
          <span>X (m)</span>
          <span>Z (m)</span>
          <span />
        </div>
        <For each={activeContour()}>
          {(p, vi) => (
            <div class="outline-editor__row" classList={{ 'is-selected': selected() === vi() }}>
              <span>{vi() + 1}</span>
              <NumberInput
                label={`Corner ${vi() + 1} X`}
                testid={`${props.testid}-x-${vi()}`}
                value={p[0]}
                step={props.snap || 0.5}
                onCommit={(v) => setCoord(Math.min(active(), props.contours.length - 1), vi(), 0, v)}
              />
              <NumberInput
                label={`Corner ${vi() + 1} Z`}
                testid={`${props.testid}-z-${vi()}`}
                value={p[1]}
                step={props.snap || 0.5}
                onCommit={(v) => setCoord(Math.min(active(), props.contours.length - 1), vi(), 1, v)}
              />
              <button
                type="button"
                class="mini"
                title="Remove this corner"
                aria-label={`Remove corner ${vi() + 1}`}
                disabled={activeContour().length <= 3}
                data-testid={`${props.testid}-remove-${vi()}`}
                onClick={() => removeVertex(Math.min(active(), props.contours.length - 1), vi())}
              >
                ×
              </button>
            </div>
          )}
        </For>
      </div>
      <div class="actions">
        <Btn
          small
          onClick={addVertex}
          disabled={activeContour().length >= props.maxPoints}
          testid={`${props.testid}-add-vertex`}
          title="Split the longest edge"
        >
          + Corner
        </Btn>
        <span class="chip">
          {activeContour().length}/{props.maxPoints}
        </span>
      </div>
    </div>
  )
}
